// Real Estate catalogue store — talks to the frozen BE contract via
// api/realEstate/*.js (never axios/the mock directly; see
// docs/real-estate/be-handover-2026-09-28.md). Field names here match the
// API exactly (snake_case, UUID ids, server-computed `rollup`) — this
// replaced an earlier version that mirrored the FE-only prototype's
// camelCase shape, before the BE handoff existed.
//
// Pattern used throughout: a mutation that only touches one or two known
// fields (setActive/setStatus) patches both caches (list + detail)
// in place; everything else that can change several nested fields at once
// (features/nearby/gallery/units/agents/operating hours/section patches)
// just re-fetches the full project detail afterwards, since the server
// recomputes derived fields (rollup, opening_hours_text, sqft, labels) on
// every read and a manual local merge would have to duplicate that logic.
import {
  getConfig,
  listProperties,
  createProperty,
  getProperty,
  patchProperty,
  setProjectStatus,
  setProjectActive,
  deleteProperty,
  listRegions,
  createFeature,
  deleteFeature,
  createNearby,
  deleteNearby,
  reorderGallery,
  updateGalleryCaption,
  deleteGalleryImage,
  setProjectAgents,
} from '../../api/realEstate/properties';
import {
  listUnits,
  createUnit,
  patchUnit,
  deleteUnit,
  reorderUnits,
} from '../../api/realEstate/units';
import {
  setCover,
  deleteCover,
  addBrochure,
  updateBrochureTitle,
  reorderBrochures,
  deleteBrochure,
  addGalleryImage,
  setFeatureIcon,
  deleteFeatureIcon,
  setNearbyIcon,
  deleteNearbyIcon,
  setUnitLayout,
  deleteUnitLayout,
  setUnitImage,
  deleteUnitImage,
} from '../../api/realEstate/media';
import {
  getOperatingHours,
  setOperatingHours,
} from '../../api/realEstate/operatingHours';
import { listAllPages } from '../../api/realEstate/pagination';

export const state = {
  config: null,
  // One page of the Projects page's filtered list.
  properties: [],
  // Every project, unfiltered — for dropdowns (appointment/calendar filters,
  // assignment and leave forms). Kept apart from `properties` so a dropdown
  // loading never replaces the Projects page's filtered page, and vice versa.
  projectOptions: [],
  propertyDetailsById: {},
  regions: [],
  uiFlags: {
    isFetchingList: false,
    isFetchingItem: false,
  },
};

export const mutations = {
  setConfig(moduleState, config) {
    moduleState.config = config;
  },
  setProperties(moduleState, properties) {
    moduleState.properties = properties;
  },
  setProjectOptions(moduleState, projects) {
    moduleState.projectOptions = projects;
  },
  // Merges a partial list-item update in place (setActive/setStatus only —
  // every other write re-fetches the full list on next visit instead of
  // trying to keep every derived field in sync locally).
  patchPropertyInList(moduleState, { property_id: propertyId, ...fields }) {
    moduleState.properties = moduleState.properties.map(p =>
      p.property_id === propertyId ? { ...p, ...fields } : p
    );
  },
  removePropertyFromList(moduleState, propertyId) {
    moduleState.properties = moduleState.properties.filter(
      p => p.property_id !== propertyId
    );
  },
  setPropertyDetail(moduleState, detail) {
    moduleState.propertyDetailsById = {
      ...moduleState.propertyDetailsById,
      [detail.property_id]: detail,
    };
  },
  patchPropertyDetail(moduleState, { property_id: propertyId, ...fields }) {
    const existing = moduleState.propertyDetailsById[propertyId];
    if (!existing) return;
    moduleState.propertyDetailsById = {
      ...moduleState.propertyDetailsById,
      [propertyId]: { ...existing, ...fields },
    };
  },
  removePropertyDetail(moduleState, propertyId) {
    const { [propertyId]: removed, ...rest } = moduleState.propertyDetailsById;
    moduleState.propertyDetailsById = rest;
  },
  setRegions(moduleState, regions) {
    moduleState.regions = regions;
  },
  setUiFlag(moduleState, { key, value }) {
    moduleState.uiFlags = { ...moduleState.uiFlags, [key]: value };
  },
};

export const getters = {
  getConfig: moduleState => moduleState.config,
  getProjects: moduleState => moduleState.properties,
  getProjectOptions: moduleState => moduleState.projectOptions,
  getProject: moduleState => propertyId =>
    moduleState.propertyDetailsById[propertyId] || null,
  getRegions: moduleState => moduleState.regions,
  getUiFlags: moduleState => moduleState.uiFlags,
};

// Shared by every action below that mutates a sub-resource (features,
// nearby, gallery, units, agents, operating hours) — re-fetches the whole
// detail so derived fields (rollup, opening_hours_text, ...) stay correct
// without duplicating the server's own computation client-side.
async function refreshProject({ dispatch }, propertyId) {
  return dispatch('fetchProject', propertyId);
}

export const actions = {
  async fetchConfig({ commit }) {
    const { data } = await getConfig();
    commit('setConfig', data);
    return data;
  },

  async fetchProjects({ commit }, query) {
    commit('setUiFlag', { key: 'isFetchingList', value: true });
    try {
      const { data } = await listProperties(query);
      commit('setProperties', data.results);
      return data;
    } finally {
      commit('setUiFlag', { key: 'isFetchingList', value: false });
    }
  },

  // All projects, every page — callers show the cached list straight away
  // and this refreshes it (names/new projects may have changed since).
  async fetchProjectOptions({ commit }) {
    const projects = await listAllPages(listProperties);
    commit('setProjectOptions', projects);
    return projects;
  },

  async fetchProject({ commit }, propertyId) {
    commit('setUiFlag', { key: 'isFetchingItem', value: true });
    try {
      const { data } = await getProperty(propertyId);
      commit('setPropertyDetail', data);
      return data;
    } finally {
      commit('setUiFlag', { key: 'isFetchingItem', value: false });
    }
  },

  // No list refresh here: the Projects page reloads its current page when
  // it's shown again, and dropdowns reload the full list when they open.
  async createProject({ commit }, fields) {
    const { data } = await createProperty(fields);
    commit('setPropertyDetail', data);
    return data;
  },

  // One form section's worth of fields (Overview/Location/Legal/Sales
  // gallery/Booking) — see FE handoff §5.
  async patchProject({ dispatch }, { propertyId, fields }) {
    await patchProperty(propertyId, fields);
    return refreshProject({ dispatch }, propertyId);
  },

  async setStatus({ commit }, { propertyId, projectStatus }) {
    const { data } = await setProjectStatus(propertyId, projectStatus);
    commit('patchPropertyInList', data);
    commit('patchPropertyDetail', data);
    return data;
  },

  async setActive({ commit }, { propertyId, isActive }) {
    const { data } = await setProjectActive(propertyId, isActive);
    commit('patchPropertyInList', data);
    commit('patchPropertyDetail', data);
    return data;
  },

  async deleteProject({ commit }, propertyId) {
    await deleteProperty(propertyId);
    commit('removePropertyFromList', propertyId);
    commit('removePropertyDetail', propertyId);
  },

  async fetchRegions({ commit }) {
    const { data } = await listRegions();
    commit('setRegions', data.regions);
    return data.regions;
  },

  // -- features --
  async addFeature({ dispatch }, { propertyId, name }) {
    await createFeature(propertyId, name);
    return refreshProject({ dispatch }, propertyId);
  },
  async removeFeature({ dispatch }, { propertyId, featureId }) {
    await deleteFeature(propertyId, featureId);
    return refreshProject({ dispatch }, propertyId);
  },
  async setFeatureIconFile({ dispatch }, { propertyId, featureId, file }) {
    await setFeatureIcon(propertyId, featureId, file);
    return refreshProject({ dispatch }, propertyId);
  },
  async removeFeatureIcon({ dispatch }, { propertyId, featureId }) {
    await deleteFeatureIcon(propertyId, featureId);
    return refreshProject({ dispatch }, propertyId);
  },

  // -- nearby --
  async addNearby({ dispatch }, { propertyId, fields }) {
    await createNearby(propertyId, fields);
    return refreshProject({ dispatch }, propertyId);
  },
  async removeNearby({ dispatch }, { propertyId, nearbyId }) {
    await deleteNearby(propertyId, nearbyId);
    return refreshProject({ dispatch }, propertyId);
  },
  async setNearbyIconFile({ dispatch }, { propertyId, nearbyId, file }) {
    await setNearbyIcon(propertyId, nearbyId, file);
    return refreshProject({ dispatch }, propertyId);
  },
  async removeNearbyIcon({ dispatch }, { propertyId, nearbyId }) {
    await deleteNearbyIcon(propertyId, nearbyId);
    return refreshProject({ dispatch }, propertyId);
  },

  // -- cover / gallery --
  // The Overview form's staged cover image, applied on Save — `cover` is
  // 'remove' or { file }.
  async applyProjectCover({ dispatch }, { propertyId, cover }) {
    try {
      await (cover === 'remove'
        ? deleteCover(propertyId)
        : setCover(propertyId, cover.file));
    } finally {
      await refreshProject({ dispatch }, propertyId);
    }
  },
  // Brochures: each write re-reads the project detail, whose presigned
  // urls expire after an hour, rather than keeping a response's url.
  async addProjectBrochure({ dispatch }, { propertyId, file, title }) {
    await addBrochure(propertyId, file, title);
    return refreshProject({ dispatch }, propertyId);
  },
  async renameProjectBrochure({ dispatch }, { propertyId, brochureId, title }) {
    await updateBrochureTitle(propertyId, brochureId, title);
    return refreshProject({ dispatch }, propertyId);
  },
  // A 422 brochure_ids_mismatch means the list changed elsewhere, so the
  // detail is re-read either way and the next try uses the fresh list.
  async reorderProjectBrochures({ dispatch }, { propertyId, brochureIds }) {
    try {
      await reorderBrochures(propertyId, brochureIds);
    } finally {
      await refreshProject({ dispatch }, propertyId);
    }
  },
  async removeProjectBrochure({ dispatch }, { propertyId, brochureId }) {
    await deleteBrochure(propertyId, brochureId);
    return refreshProject({ dispatch }, propertyId);
  },
  async addGalleryImageFile({ dispatch }, { propertyId, file, caption }) {
    await addGalleryImage(propertyId, file, caption);
    return refreshProject({ dispatch }, propertyId);
  },
  async setGalleryCaption({ dispatch }, { propertyId, imageId, caption }) {
    await updateGalleryCaption(propertyId, imageId, caption);
    return refreshProject({ dispatch }, propertyId);
  },
  async reorderGalleryImages({ dispatch }, { propertyId, imageIds }) {
    await reorderGallery(propertyId, imageIds);
    return refreshProject({ dispatch }, propertyId);
  },
  async removeGalleryImage({ dispatch }, { propertyId, imageId }) {
    await deleteGalleryImage(propertyId, imageId);
    return refreshProject({ dispatch }, propertyId);
  },

  // -- floor plans (units) --
  async fetchUnits(context, propertyId) {
    const { data } = await listUnits(propertyId);
    return data.results;
  },
  // Returns the created floor plan (not the refreshed project) so the
  // caller can upload its image/layout against the new unit_id.
  async createUnitForProject({ dispatch }, { propertyId, fields }) {
    const { data } = await createUnit(propertyId, fields);
    await refreshProject({ dispatch }, propertyId);
    return data;
  },
  async patchUnitForProject({ dispatch }, { propertyId, unitId, fields }) {
    await patchUnit(propertyId, unitId, fields);
    return refreshProject({ dispatch }, propertyId);
  },
  async removeUnit({ dispatch }, { propertyId, unitId }) {
    await deleteUnit(propertyId, unitId);
    return refreshProject({ dispatch }, propertyId);
  },
  async reorderProjectUnits({ dispatch }, { propertyId, unitIds }) {
    await reorderUnits(propertyId, unitIds);
    return refreshProject({ dispatch }, propertyId);
  },
  async setUnitLayoutFile({ dispatch }, { propertyId, unitId, file }) {
    await setUnitLayout(propertyId, unitId, file);
    return refreshProject({ dispatch }, propertyId);
  },
  async removeUnitLayout({ dispatch }, { propertyId, unitId }) {
    await deleteUnitLayout(propertyId, unitId);
    return refreshProject({ dispatch }, propertyId);
  },
  async setUnitImageFile({ dispatch }, { propertyId, unitId, file }) {
    await setUnitImage(propertyId, unitId, file);
    return refreshProject({ dispatch }, propertyId);
  },
  async removeUnitImage({ dispatch }, { propertyId, unitId }) {
    await deleteUnitImage(propertyId, unitId);
    return refreshProject({ dispatch }, propertyId);
  },
  // UnitModal's Save: applies the staged unit image and layout changes
  // (each `{ file }` to upload, `'remove'` to delete, or null to leave
  // alone) with one refresh at the end. Stops at the first failure and
  // reports which slot failed via err.mediaSlot; the project is still
  // refreshed so whatever did succeed shows up.
  async applyUnitMedia({ dispatch }, { propertyId, unitId, image, layout }) {
    const apply = async (slot, change, upload, remove) => {
      if (!change) return;
      try {
        await (change === 'remove'
          ? remove(propertyId, unitId)
          : upload(propertyId, unitId, change.file));
      } catch (err) {
        err.mediaSlot = slot;
        throw err;
      }
    };
    try {
      await apply('image', image, setUnitImage, deleteUnitImage);
      await apply('layout', layout, setUnitLayout, deleteUnitLayout);
    } finally {
      await refreshProject({ dispatch }, propertyId);
    }
  },

  // -- assigned agents --
  async setProjectAgentIds({ dispatch }, { propertyId, agentIds }) {
    await setProjectAgents(propertyId, agentIds);
    return refreshProject({ dispatch }, propertyId);
  },

  // -- Layer 0: opening hours --
  async fetchOperatingHours(context, propertyId) {
    const { data } = await getOperatingHours(propertyId);
    return data;
  },
  async saveOperatingHours({ dispatch }, { propertyId, hours }) {
    await setOperatingHours(propertyId, hours);
    return refreshProject({ dispatch }, propertyId);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
